/**
 * TCK customer survey renderer.
 *
 * Renders a survey built in the production planner. The page carries no survey
 * content of its own: everything is fetched from the planner API using the
 * token in the ?s= query param, so the bare page renders nothing and the URL
 * is only useful to people we've given it to (link or QR code).
 *
 * Expects a container: <div id="tck-survey" data-api="https://planner-app"></div>
 */
(function () {
  "use strict";

  var container = document.getElementById("tck-survey");
  if (!container) return;

  var API = container.getAttribute("data-api") || "";
  // Token comes from the shared link/QR (?s=), or from a token pinned on the
  // section — used on the members-only Calzone Club page, where Locksmith
  // membership gating stands in for the unguessable link.
  var token = new URLSearchParams(window.location.search).get("s")
    || container.getAttribute("data-token") || "";

  // No API configured or no token: leave the page silently empty.
  if (!API || !token) return;

  var DONE_KEY = "tck-survey-done-" + token;
  var CLIENT_KEY = "tck-survey-client";

  function clientId() {
    var id = null;
    try { id = localStorage.getItem(CLIENT_KEY); } catch (e) {}
    if (!id) {
      id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      try { localStorage.setItem(CLIENT_KEY, id); } catch (e) {}
    }
    return id;
  }

  var survey = null;
  var answers = {}; // question id -> value

  // ---------- rendering helpers ----------

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function message(title, body) {
    container.innerHTML = "";
    var wrap = el("div", "tck-survey__message");
    wrap.appendChild(el("h2", "tck-survey__message-title custom-font", title));
    if (body) wrap.appendChild(el("p", "tck-survey__message-body", body));
    container.appendChild(wrap);
  }

  // Optional final offer page after submit (Graeme, 2026-10-16) — set per
  // survey in the planner; null when switched off.
  function finalPage(page) {
    container.innerHTML = "";
    var wrap = el("div", "tck-survey__final");
    if (page.heading) wrap.appendChild(el("h2", "tck-survey__final-title custom-font", page.heading));
    if (page.html) {
      var body = el("div", "tck-survey__final-body tck-rich");
      body.innerHTML = page.html; // sanitised by the planner API
      wrap.appendChild(body);
    }
    if (page.offer) {
      var offer = el("div", "tck-survey__final-offer");
      offer.appendChild(el("p", "tck-survey__final-offer-label", "This week's offer"));
      offer.appendChild(el("p", "tck-survey__final-offer-text", page.offer));
      wrap.appendChild(offer);
    }
    if (page.button && /^https:\/\/(www\.)?thecalzonekitchen\.co\.uk\//.test(page.button.url)) {
      var a = el("a", "tck-survey__final-btn", page.button.label);
      a.href = page.button.url;
      wrap.appendChild(a);
    }
    container.appendChild(wrap);
    window.scrollTo({ top: container.getBoundingClientRect().top + window.pageYOffset - 40, behavior: "smooth" });
  }

  // ---------- question renderers ----------
  // Each returns a node and registers reads/writes on `answers[q.id]`.

  function renderRating(q) {
    // Stars only make sense at small scales — clamp whatever the API sends.
    // Granular 0-100 opinions are the slider type's job.
    var max = Math.min(Math.max(q.max || 5, 2), 10);
    var wrap = el("div", "tck-survey__stars");
    wrap.setAttribute("role", "radiogroup");
    wrap.setAttribute("aria-label", q.prompt);
    var buttons = [];
    for (var i = 1; i <= max; i++) {
      (function (value) {
        var b = el("button", "tck-survey__star", "★");
        b.type = "button";
        b.setAttribute("aria-label", value + " out of " + max);
        b.addEventListener("click", function () {
          answers[q.id] = value;
          buttons.forEach(function (btn, idx) {
            btn.classList.toggle("is-active", idx < value);
          });
        });
        buttons.push(b);
        wrap.appendChild(b);
      })(i);
    }
    return wrap;
  }

  function renderChoice(q, multi) {
    var wrap = el("div", "tck-survey__options");
    (q.options || []).forEach(function (opt) {
      var b = el("button", "tck-survey__option alt-focus", opt);
      b.type = "button";
      b.addEventListener("click", function () {
        if (multi) {
          var current = answers[q.id] || [];
          var idx = current.indexOf(opt);
          if (idx === -1) current.push(opt); else current.splice(idx, 1);
          answers[q.id] = current;
          b.classList.toggle("is-active");
        } else {
          answers[q.id] = opt;
          Array.prototype.forEach.call(wrap.children, function (c) {
            c.classList.toggle("is-active", c === b);
          });
        }
      });
      wrap.appendChild(b);
    });
    return wrap;
  }

  function renderText(q) {
    var t = el("textarea", "tck-survey__textarea");
    t.rows = 4;
    t.maxLength = 2000;
    t.placeholder = "Type your answer…";
    t.addEventListener("input", function () {
      answers[q.id] = t.value.trim();
    });
    return t;
  }

  function renderRank(q) {
    // Order is the answer; ▲▼ to reorder. Pre-filled so rank questions
    // always have a valid value once touched or left alone.
    var order = (q.options || []).slice();
    answers[q.id] = order.slice();
    var list = el("div", "tck-survey__rank");

    function redraw() {
      list.innerHTML = "";
      order.forEach(function (opt, idx) {
        var row = el("div", "tck-survey__rank-row");
        row.appendChild(el("span", "tck-survey__rank-pos", String(idx + 1)));
        row.appendChild(el("span", "tck-survey__rank-label", opt));
        var controls = el("span", "tck-survey__rank-controls");
        var up = el("button", "tck-survey__rank-btn", "▲");
        up.type = "button";
        up.disabled = idx === 0;
        up.setAttribute("aria-label", "Move " + opt + " up");
        up.addEventListener("click", function () {
          order.splice(idx - 1, 0, order.splice(idx, 1)[0]);
          answers[q.id] = order.slice();
          redraw();
        });
        var down = el("button", "tck-survey__rank-btn", "▼");
        down.type = "button";
        down.disabled = idx === order.length - 1;
        down.setAttribute("aria-label", "Move " + opt + " down");
        down.addEventListener("click", function () {
          order.splice(idx + 1, 0, order.splice(idx, 1)[0]);
          answers[q.id] = order.slice();
          redraw();
        });
        controls.appendChild(up);
        controls.appendChild(down);
        row.appendChild(controls);
        list.appendChild(row);
      });
    }
    redraw();
    return list;
  }

  function renderSlider(q) {
    // 0-100 approval slider. No value is recorded until the customer moves
    // (or taps) it, so an untouched required slider still fails validation.
    var wrap = el("div", "tck-survey__slider");

    var readout = el("div", "tck-survey__slider-readout");
    var value = el("span", "tck-survey__slider-value", "—");
    readout.appendChild(value);
    readout.appendChild(el("span", "tck-survey__slider-unit", "%"));
    var hint = el("p", "tck-survey__slider-hint", "Slide to rate from 0 to 100");

    var input = el("input", "tck-survey__slider-input");
    input.type = "range";
    input.min = "0";
    input.max = "100";
    input.step = "1";
    input.value = "50";
    input.setAttribute("aria-label", q.prompt + " — approval from 0 to 100");

    function record() {
      var v = Number(input.value);
      answers[q.id] = v;
      value.textContent = String(v);
      wrap.classList.add("is-touched");
      input.style.setProperty("--fill", v + "%");
    }
    input.addEventListener("input", record);
    // A tap that doesn't move the handle still counts as choosing the value.
    input.addEventListener("change", record);
    input.style.setProperty("--fill", "50%");

    var scale = el("div", "tck-survey__slider-scale");
    scale.appendChild(el("span", null, "0%"));
    scale.appendChild(el("span", null, "100%"));

    wrap.appendChild(readout);
    wrap.appendChild(input);
    wrap.appendChild(scale);
    wrap.appendChild(hint);
    return wrap;
  }

  var RENDERERS = {
    rating: function (q) { return renderRating(q); },
    choice: function (q) { return renderChoice(q, false); },
    multi: function (q) { return renderChoice(q, true); },
    text: function (q) { return renderText(q); },
    rank: function (q) { return renderRank(q); },
    slider: function (q) { return renderSlider(q); },
  };

  // ---------- validation ----------

  function isMissing(q) {
    if (!q.required) return false;
    var v = answers[q.id];
    if (v == null) return true;
    if (typeof v === "string") return v === "";
    if (Array.isArray(v)) return v.length === 0;
    return false;
  }

  // ---------- pages ----------
  // Consecutive questions about the same recipe share a step (so a product's
  // approval slider and its notes sit together); consecutive general
  // questions share a step too.

  function buildPages(questions) {
    var pages = [];
    var current = null;
    questions.forEach(function (q) {
      var key = q.recipe ? "recipe-" + q.recipe.id : "general";
      if (!current || current.key !== key) {
        current = { key: key, recipe: q.recipe || null, questions: [] };
        pages.push(current);
      }
      current.questions.push(q);
    });
    return pages;
  }

  function renderSurvey(questions, followUp) {
    container.innerHTML = "";
    var pages = buildPages(questions);
    var pageIndex = 0;

    var form = el("div", "tck-survey__form");

    var head = el("header", "tck-survey__head");
    head.appendChild(el("h1", "tck-survey__title custom-font", survey.title));
    var intro = null;
    var introText = survey.intro;
    if (followUp) {
      // Completion visit: only their unanswered questions are rendered.
      var recipeIds = {};
      var recipeCount = 0;
      questions.forEach(function (q) {
        if (q.recipe && !recipeIds[q.recipe.id]) { recipeIds[q.recipe.id] = 1; recipeCount++; }
      });
      introText = recipeCount > 0
        ? "Welcome back! You skipped " + recipeCount + (recipeCount === 1 ? " product" : " products")
          + " last time — tried " + (recipeCount === 1 ? "it" : "them") + " since? "
          + "Your other answers are safely saved."
        : "Welcome back! Just a few questions left from last time — your other answers are safely saved.";
    }
    if (!followUp && survey.intro_html) {
      // Sanitised by the planner API (strict allow-list, re-applied on output).
      intro = el("div", "tck-survey__intro tck-rich");
      intro.innerHTML = survey.intro_html;
      head.appendChild(intro);
    } else if (introText) {
      intro = el("p", "tck-survey__intro", introText);
      head.appendChild(intro);
    }
    form.appendChild(head);

    var progress = el("div", "tck-survey__progress");
    var progressLabel = el("p", "tck-survey__progress-label");
    var progressBar = el("div", "tck-survey__progress-bar");
    var progressFill = el("div", "tck-survey__progress-fill");
    progressBar.appendChild(progressFill);
    progress.appendChild(progressLabel);
    progress.appendChild(progressBar);
    form.appendChild(progress);

    var pagesWrap = el("div", "tck-survey__pages");
    var pageNodes = pages.map(function (page) {
      var node = el("div", "tck-survey__page");
      if (page.recipe) {
        var card = el("div", "tck-survey__recipe");
        if (page.recipe.image_url) {
          var img = el("img", "tck-survey__recipe-img");
          img.src = page.recipe.image_url;
          img.alt = page.recipe.name;
          img.loading = "lazy";
          card.appendChild(img);
        }
        card.appendChild(el("h2", "tck-survey__recipe-name custom-font", page.recipe.name));
        node.appendChild(card);
      }
      page.questions.forEach(function (q) {
        var block = el("div", "tck-survey__question");
        block.setAttribute("data-question-id", q.id);
        var label = el("p", "tck-survey__prompt", q.prompt);
        if (q.required) label.appendChild(el("span", "tck-survey__required", " *"));
        block.appendChild(label);
        var renderer = RENDERERS[q.type];
        if (renderer) block.appendChild(renderer(q));
        node.appendChild(block);
      });
      pagesWrap.appendChild(node);
      return node;
    });
    form.appendChild(pagesWrap);

    var footer = el("div", "tck-survey__footer");
    var errorBox = el("p", "tck-survey__error");
    errorBox.setAttribute("role", "alert");
    var nav = el("div", "tck-survey__nav");
    var backBtn = el("button", "tck-survey__back", "Back");
    backBtn.type = "button";
    // "Didn't try it" escape hatch, recipe steps only: excuses that step's
    // required questions and reports the skip as data. Toggles, in case they
    // change their mind and come back.
    var skipWrap = el("div", "tck-survey__skip-wrap");
    var skipBtn = el("button", "tck-survey__skip", "Skip");
    skipBtn.type = "button";
    var skipCaption = el("span", "tck-survey__skip-caption", "If you haven't tried this recipe");
    skipWrap.appendChild(skipBtn);
    skipWrap.appendChild(skipCaption);
    var nextBtn = el("button", "tck-survey__submit", "Next");
    nextBtn.type = "button";
    nav.appendChild(backBtn);
    nav.appendChild(skipWrap);
    nav.appendChild(nextBtn);
    footer.appendChild(errorBox);
    footer.appendChild(nav);
    form.appendChild(footer);

    container.appendChild(form);

    function clearHighlights() {
      form.querySelectorAll(".tck-survey__question.is-missing").forEach(function (n) {
        n.classList.remove("is-missing");
      });
      errorBox.textContent = "";
    }

    function highlightMissing(missing) {
      missing.forEach(function (q) {
        var node = form.querySelector('[data-question-id="' + q.id + '"]');
        if (node) node.classList.add("is-missing");
      });
      errorBox.textContent = "Please answer the highlighted question" + (missing.length > 1 ? "s" : "") + ".";
    }

    function show(idx, direction) {
      pageIndex = idx;
      clearHighlights();
      pageNodes.forEach(function (n, i) {
        n.classList.toggle("is-active", i === idx);
        n.classList.remove("tck-slide-forward", "tck-slide-back");
      });
      // Force a reflow so re-adding the animation class replays it.
      void pageNodes[idx].offsetWidth;
      pageNodes[idx].classList.add(direction === "back" ? "tck-slide-back" : "tck-slide-forward");

      progressLabel.textContent = "Step " + (idx + 1) + " of " + pages.length;
      progressFill.style.width = (((idx + 1) / pages.length) * 100) + "%";
      if (intro) intro.style.display = idx === 0 ? "" : "none";
      backBtn.style.visibility = idx === 0 ? "hidden" : "visible";
      nextBtn.textContent = idx === pages.length - 1 ? "Send my feedback" : "Next";
      updateSkipUI();
    }

    function updateSkipUI() {
      var page = pages[pageIndex];
      skipWrap.style.display = page.recipe ? "" : "none";
      skipBtn.classList.toggle("is-skipped", !!page.skipped);
      skipBtn.textContent = page.skipped ? "Skipped ✓" : "Skip";
      skipCaption.textContent = page.skipped
        ? "Tap again if you did try it"
        : "If you haven't tried this recipe";

      var top = container.getBoundingClientRect().top;
      if (top < 0) container.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function send() {
      // Whole-survey safety net: jump back to the first step with a gap.
      for (var i = 0; i < pages.length; i++) {
        if (pages[i].skipped) continue;
        var missing = pages[i].questions.filter(isMissing);
        if (missing.length) {
          show(i, i < pageIndex ? "back" : "forward");
          highlightMissing(missing);
          return;
        }
      }

      nextBtn.disabled = true;
      backBtn.disabled = true;
      nextBtn.textContent = "Sending…";

      var skippedIds = [];
      pages.forEach(function (page) {
        if (page.skipped) {
          page.questions.forEach(function (q) { skippedIds.push(q.id); });
        }
      });

      var payload = {
        client_id: clientId(),
        answers: Object.keys(answers)
          .filter(function (id) { return skippedIds.indexOf(Number(id)) === -1; })
          .map(function (id) {
            return { question_id: Number(id), value: answers[id] };
          }),
        skipped: skippedIds,
      };

      fetch(API + "/api/public/surveys/" + encodeURIComponent(token) + "/responses", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (res.status === 409) {
            try { localStorage.setItem(DONE_KEY, "1"); } catch (e) {}
            message("Thanks — we've already got your answers!", "You can only fill this one in once.");
            return;
          }
          if (!res.ok) throw new Error("HTTP " + res.status);
          try { localStorage.setItem(DONE_KEY, "1"); } catch (e) {}
          if (survey.final_page) {
            finalPage(survey.final_page);
          } else if (followUp) {
            message("Lovely — thanks!", "We've added your thoughts on the ones you tried.");
          } else {
            message("Thank you!", "Your feedback goes straight to our kitchen and genuinely shapes what we bake next.");
          }
        })
        .catch(function () {
          nextBtn.disabled = false;
          backBtn.disabled = false;
          nextBtn.textContent = "Send my feedback";
          errorBox.textContent = "Sorry — that didn't send. Please check your connection and try again.";
        });
    }

    nextBtn.addEventListener("click", function () {
      clearHighlights();
      if (!pages[pageIndex].skipped) {
        var missing = pages[pageIndex].questions.filter(isMissing);
        if (missing.length) {
          highlightMissing(missing);
          return;
        }
      }
      if (pageIndex === pages.length - 1) send();
      else show(pageIndex + 1, "forward");
    });

    backBtn.addEventListener("click", function () {
      if (pageIndex > 0) show(pageIndex - 1, "back");
    });

    skipBtn.addEventListener("click", function () {
      var page = pages[pageIndex];
      if (!page.recipe) return;
      page.skipped = !page.skipped;
      pageNodes[pageIndex].classList.toggle("is-skipped", page.skipped);
      clearHighlights();
      updateSkipUI();
      if (page.skipped && pageIndex < pages.length - 1) show(pageIndex + 1, "forward");
    });

    show(0, "forward");
  }

  // ---------- boot ----------
  // The API is the authority on whether this browser has responded before:
  // `previous` carries answered/skipped question ids (never values), so a
  // returning visitor can top up skipped questions without editing anything.

  fetch(API + "/api/public/surveys/" + encodeURIComponent(token) + "?client_id=" + encodeURIComponent(clientId()))
    .then(function (res) {
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    })
    .then(function (data) {
      if (!data) return; // unknown token: stay silently empty
      if (data.status === "closed") {
        message("This survey has closed", "Thanks for your interest — keep an eye out for the next one!");
        return;
      }
      survey = data;
      var prev = data.previous;
      if (!prev) {
        renderSurvey(survey.questions, false);
        return;
      }
      var skippedBefore = prev.skipped || [];
      if (!skippedBefore.length) {
        message("Thanks — we've already got your answers!", "You can only fill this one in once.");
        return;
      }
      // Completion visit: everything they haven't answered yet — the skips,
      // plus any question they never saw or left blank.
      var answeredBefore = {};
      (prev.answered || []).forEach(function (id) { answeredBefore[id] = 1; });
      var remaining = survey.questions.filter(function (q) { return !answeredBefore[q.id]; });
      if (!remaining.length) {
        message("Thanks — we've already got your answers!", "You can only fill this one in once.");
        return;
      }
      renderSurvey(remaining, true);
    })
    .catch(function () {
      message("Something went wrong", "We couldn't load this survey just now. Please try the link again in a minute.");
    });
})();
